import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Flame, X, ChevronRight, Clock } from "lucide-react";
import { FESTIVALS, nextUpcoming, festivalsForAirport, SEVERITY_STYLES } from "../data/indianFestivals";

/* <FestivalStrip /> — slim strip rendered on home when a festival peak is
   within 14 days. Dismissable per session. */
export function FestivalStrip() {
  const [festival, setFestival] = useState(null);

  useEffect(() => {
    const upcoming = nextUpcoming();
    if (!upcoming) return;
    const dismissed = (sessionStorage.getItem("festival_dismissed") || "").split(",");
    if (!dismissed.includes(upcoming.id)) setFestival(upcoming);
  }, []);

  if (!festival) return null;
  const sty = SEVERITY_STYLES[festival.severity] || SEVERITY_STYLES.medium;

  const dismiss = () => {
    const d = (sessionStorage.getItem("festival_dismissed") || "").split(",").filter(Boolean);
    d.push(festival.id);
    sessionStorage.setItem("festival_dismissed", d.join(","));
    setFestival(null);
  };

  return (
    <div data-testid="festival-strip" className={`w-full ${sty.bg} border-b ${sty.border}`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-5 py-2 flex items-center gap-2 text-xs sm:text-[13px]">
        <span className={`inline-flex items-center gap-1.5 ${sty.pill} text-white rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] font-900 uppercase tracking-wider shrink-0`}>
          <Flame className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> {festival.severity === "high" ? "Peak alert" : "Travel notice"}
        </span>
        <span className={`font-700 flex-1 min-w-0 ${sty.text}`}>
          <b>{festival.name}</b> · {new Date(festival.date).toLocaleDateString(undefined, { month: "long", day: "numeric" })} —
          <span className="font-500 ml-1 hidden sm:inline">{festival.note.slice(0, 110)}{festival.note.length > 110 ? "…" : ""}</span>
          <span className="font-500 ml-1 sm:hidden">peak {festival.peak_window.join(" → ")}</span>
        </span>
        <Link
          to="/travel-to-india/festivals"
          data-testid="festival-strip-link"
          className={`shrink-0 inline-flex items-center gap-0.5 ${sty.pill} text-white rounded-full px-2 py-0.5 text-[11px] font-800 hover:brightness-110`}
        >
          See all <ChevronRight className="w-3 h-3" />
        </Link>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="festival-strip-dismiss"
          className={`shrink-0 w-5 h-5 rounded-full ${sty.text} hover:bg-white/40 inline-flex items-center justify-center`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

/* <FestivalAirportCallout iata="DEL" /> — compact card for airport detail
   pages showing festivals that peak at that airport within 60 days. */
export function FestivalAirportCallout({ iata }) {
  const [open, setOpen] = useState(true);
  const upcoming = useMemo(() => {
    const now = new Date();
    const target = new Date(now.getTime() + 60 * 86400000);
    return festivalsForAirport(iata).filter((f) => {
      const [a, b] = f.peak_window.map((d) => new Date(d));
      return b >= now && a <= target;
    }).sort((a, b) => new Date(a.peak_window[0]) - new Date(b.peak_window[0])).slice(0, 2);
  }, [iata]);

  if (!open || !upcoming.length) return null;
  return (
    <div data-testid={`festival-callout-${iata}`} className="rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <span className="inline-flex p-2 rounded-xl bg-amber-500 text-white"><Calendar className="w-5 h-5" /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading font-900 text-base sm:text-lg text-zinc-900">Upcoming peaks at {iata}</h3>
          <div className="mt-2 space-y-2">
            {upcoming.map((f) => {
              const sty = SEVERITY_STYLES[f.severity];
              return (
                <div key={f.id} className="rounded-2xl bg-white border border-zinc-200 p-3" data-testid={`festival-callout-item-${f.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <div className="font-800 text-sm text-zinc-900">{f.name}</div>
                      <div className="text-[11px] text-zinc-500 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {new Date(f.peak_window[0]).toLocaleDateString(undefined, { month: "short", day: "numeric" })} — {new Date(f.peak_window[1]).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </div>
                    </div>
                    <span className={`inline-flex items-center ${sty.pill} text-white rounded-full px-2 py-0.5 text-[10px] font-800 uppercase tracking-wider`}>{f.severity} peak</span>
                  </div>
                  <p className="mt-1.5 text-xs text-zinc-700 leading-relaxed">{f.note}</p>
                  <p className="mt-1 text-[11px] text-emerald-800 font-700">Off-peak: {f.off_peak}</p>
                </div>
              );
            })}
          </div>
          <Link
            to="/travel-to-india/festivals"
            data-testid={`festival-callout-see-all-${iata}`}
            className="mt-3 inline-flex items-center gap-1 text-xs font-800 text-blue-700 hover:underline"
          >
            See the full festival calendar <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <button
          onClick={() => setOpen(false)}
          aria-label="Dismiss"
          className="shrink-0 w-5 h-5 rounded-full hover:bg-amber-200 inline-flex items-center justify-center"
        >
          <X className="w-3 h-3 text-amber-800" />
        </button>
      </div>
    </div>
  );
}
